import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { useInboxReplyContext } from "app/store/InboxReplyContext";
import MKLoader from "components/loader/MKLoader";
import FileUploadErrorModal from "components/shared/fileUploadErrorModal/FileUploadErrorModal";
import { useRequiredCustomerId } from "hooks/use-customer-id";
import { useIsMobile } from "hooks/use-is-mobile";
import useSetErrorOnUpload from "hooks/use-set-error-on-upload";
import { Attachment } from "models/dashboard/dashboard";
import { DocumentUploadFile } from "models/DocumentUploadFile";
import { useRootStore } from "store";
import DiscardMessageModal from "views/inbox/DiscardMessageModal";
import CreateThreadDesktop from "./createThread/desktop";
import CreateThreadMobile from "./createThread/mobile";

type File = {
  id: string;
  name: string;
  type: string;
};

export default function MessageEditor() {
  const navigate = useNavigate();
  const customerId = useRequiredCustomerId();
  const isMobile = useIsMobile();
  const {
    isDirty,
    clearDirty,
    resetFormState,
    formContextId,
    setFormContextId
  } = useInboxReplyContext();

  const { user, archive, inbox } = useRootStore((store) => store);

  // Reset form when navigating to new message (context changes from thread to 'new')
  // This persists on resize (formContextId stays 'new' in context)
  useEffect(() => {
    if (formContextId !== "new") {
      resetFormState();
      setFormContextId("new");
    }
  }, [formContextId, resetFormState, setFormContextId]);

  // Don't render form until context is ready (form has been reset)
  const isFormReady = formContextId === "new";

  const { sendFileToArchive } = archive;

  const { fetchThread, loadInbox, sendMessage, activeFilters } = inbox;

  const [isSending, setIsSending] = useState(false);
  const [showConfirmDiscardModal, setShowConfirmDiscardModal] = useState(false);

  const { failed, setFailed, failMessage, setFailedWrapper } =
    useSetErrorOnUpload();

  const hasActiveFilters = (): boolean => {
    const { flagged, unread, drafts, archived, categories } = activeFilters;

    return flagged || unread || drafts || archived || categories.length > 0;
  };

  const handleOnSend = async (
    title: string,
    body: string,
    recipients: string[],
    attachments: Attachment[],
    filesToUpload: DocumentUploadFile[],
    type?: string,
    reportStart?: string,
    reportEnd?: string,
    category?: number
  ) => {
    setIsSending(true);

    try {
      const result = (await sendFileToArchive(
        filesToUpload,
        customerId,
        "Inbox",
        setFailedWrapper
      )) as unknown;
      if (!result) {
        setIsSending(false);
        return;
      }
      const files = result as File[];
      let uploadedAttachments: File[] = [];
      if (files && files.length > 0) {
        uploadedAttachments = files.map((file) => {
          return { id: file.id, name: file.name, type: file.type };
        });
      }

      const allAttachments = [...attachments, ...uploadedAttachments];

      const sendMessageResult = (await sendMessage(
        user.userInfo?.id ?? "",
        body,
        title,
        null,
        recipients,
        customerId,
        allAttachments,
        type,
        reportStart,
        reportEnd,
        category
      )) as any;

      setIsSending(false);
      loadInbox(customerId);

      if (sendMessageResult) {
        clearDirty();
        resetFormState();
        fetchThread(customerId, sendMessageResult.threadId);
        if (hasActiveFilters()) {
          navigate(`/customers/${customerId}/inbox/my`);
        } else {
          navigate(
            `/customers/${customerId}/inbox/my/threads/${encodeURIComponent(sendMessageResult.threadId)}`
          );
        }
      }
    } catch (error) {
      setIsSending(false);
      console.error("Error sending message or uploading files:", error);
    }
  };

  const handleDiscardClicked = () => {
    if (isDirty) {
      setShowConfirmDiscardModal(true);
      return;
    }
    navigate(-1);
  };
  const handleConfirmDiscard = () => {
    clearDirty();
    resetFormState();
    setShowConfirmDiscardModal(false);
    navigate(-1);
  };

  return (
    <div
      className={`inbox-content-wrapper inbox-content-wrapper-new-message d-flex w-100`}
    >
      {failed && (
        <FileUploadErrorModal failMessage={failMessage} setFailed={setFailed} />
      )}

      {isSending && !failed && (
        <MKLoader show={isSending} message="Skickar meddelande..." />
      )}

      {showConfirmDiscardModal && (
        <DiscardMessageModal
          onDismiss={() => setShowConfirmDiscardModal(false)}
          onCancel={() => setShowConfirmDiscardModal(false)}
          onConfirm={handleConfirmDiscard}
        />
      )}

      {isFormReady &&
        (isMobile ? (
          <CreateThreadMobile
            isSending={isSending}
            showSubjectLine
            showBackIcon
            defaultRecipients={[]}
            onSend={handleOnSend}
            onDiscardClicked={handleDiscardClicked}
          />
        ) : (
          <CreateThreadDesktop
            isSending={isSending}
            showSubjectLine
            defaultRecipients={[]}
            onSend={handleOnSend}
            onDiscardClicked={handleDiscardClicked}
          />
        ))}
    </div>
  );
}
